import { supabase, Event, Registration, User, Minor, EventDistance } from '../lib/supabase'

// Event operations
export const eventService = {
  // Get all events (only those with goreg = 'yes')
  async getAllEvents(): Promise<Event[]> {
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .eq('goreg', 'yes')
      .order('date', { ascending: true })
    
    if (error) {
      console.error('Error fetching events:', error)
      throw error
    }
    
    return data || []
  },

  // Get events by coordinator ID (for coordinator dashboard)
  async getEventsByCoordinator(coordinatorId: string): Promise<Event[]> {
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .eq('organizer_id', coordinatorId)
      .eq('goreg', 'yes')
      .order('date', { ascending: true })
    
    if (error) {
      console.error('Error fetching coordinator events:', error)
      // Don't throw error if it's just "no rows found"
      if (error.code === 'PGRST116' || error.message?.includes('No rows')) {
        return []
      }
      throw error
    }
    
    return data || []
  },

  // Get event by ID (only if goreg = 'yes')
  async getEventById(id: string): Promise<Event | null> {
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .eq('id', id)
      .eq('goreg', 'yes')
      .single()
    
    if (error) {
      console.error('Error fetching event:', error)
      throw error
    }
    
    return data
  },

  // Get event distances for a specific event
  async getEventDistances(eventId: string): Promise<EventDistance[]> {
    const { data, error } = await supabase
      .from('event_distances')
      .select('*')
      .eq('event_id', eventId)
      .order('price', { ascending: true })
    
    if (error) {
      console.error('Error fetching event distances:', error)
      throw error
    }
    
    return data || []
  },

  // Create new event
  async createEvent(event: Omit<Event, 'id' | 'created_at' | 'updated_at'>): Promise<Event> {
    try {
      // If organizer_id is provided, ensure the organizer record exists
      if (event.organizer_id) {
        // Check if organizer record exists
        const { data: existingOrganizer } = await supabase
          .from('event_organizers')
          .select('id')
          .eq('id', event.organizer_id)
          .single();

        if (!existingOrganizer) {
          // Get user details to create organizer record
          const { data: userData } = await supabase
            .from('users')
            .select('name, email, phone')
            .eq('id', event.organizer_id)
            .single();

          if (userData) {
            // Check if this is an admin user
            const { data: coordinatorProfile } = await supabase
              .from('coordinator_profiles')
              .select('role')
              .eq('user_email', userData.email)
              .eq('status', 'active')
              .single();

            const isAdmin = coordinatorProfile?.role === 'admin';
            
            // Create organizer record with appropriate payment configuration
            const { error: organizerError } = await supabase
              .from('event_organizers')
              .insert({
                id: event.organizer_id,
                name: userData.name,
                email: userData.email,
                phone: userData.phone || '+27000000000',
                organization: isAdmin ? 'GOMTB Admin' : 'GOMTB Event Coordinator',
                payment_gateway: isAdmin ? 'payfast' : 'manual',
                payment_config: isAdmin ? {
                  merchant_id: process.env.REACT_APP_ADMIN_PAYFAST_MERCHANT_ID || '10000100',
                  merchant_key: process.env.REACT_APP_ADMIN_PAYFAST_MERCHANT_KEY || '46f0cd694581a',
                  passphrase: process.env.REACT_APP_ADMIN_PAYFAST_PASSPHRASE || '',
                  sandbox: (process.env.REACT_APP_PAYFAST_ENVIRONMENT || 'sandbox') === 'sandbox',
                  is_admin_config: true
                } : {
                  banking_details: {
                    bank_name: 'To be configured',
                    account_holder: userData.name,
                    account_number: 'To be configured',
                    branch_code: 'To be configured',
                    account_type: 'To be configured',
                    reference_format: 'Event Registration - {event_title}'
                  }
                },
                is_verified: true,
                created_at: new Date().toISOString()
              });

            if (organizerError) {
              console.error('Error creating organizer record:', organizerError);
              // Continue without organizer_id if we can't create the record
              delete event.organizer_id;
            } else {
              console.log(`Admin PayFast configuration automatically assigned for admin user: ${userData.email}`);
            }
          } else {
            // If we can't find user data, remove organizer_id
            delete event.organizer_id;
          }
        }
      }

      const eventData = {
        ...event,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('events')
        .insert([eventData])
        .select()
        .single()
      
      if (error) {
        console.error('Error creating event:', error)
        throw error
      }
      
      return data
    } catch (error) {
      console.error('Error in createEvent:', error)
      throw error
    }
  },

  // Update event
  async updateEvent(id: string, updates: Partial<Event>): Promise<Event> {
    const { data, error } = await supabase
      .from('events')
      .update(updates)
      .eq('id', id)
      .select()
      .single()
    
    if (error) {
      console.error('Error updating event:', error)
      throw error
    }
    
    return data
  },

  // Delete event
  async deleteEvent(id: string): Promise<void> {
    const { error } = await supabase
      .from('events')
      .delete()
      .eq('id', id)
    
    if (error) {
      console.error('Error deleting event:', error)
      throw error
    }
  },

  // Create event distances
  async createEventDistances(eventId: string, distances: Array<{
    name: string;
    length: string;
    price: number;
    description?: string;
    age_restriction?: string;
    difficulty?: string;
  }>): Promise<EventDistance[]> {
    const distanceData = distances.map(distance => ({
      event_id: eventId,
      name: distance.name,
      length: distance.length,
      price: distance.price,
      description: distance.description || '',
      age_restriction: distance.age_restriction || '12+',
      difficulty: distance.difficulty || 'Intermediate',
      created_at: new Date().toISOString()
    }));

    const { data, error } = await supabase
      .from('event_distances')
      .insert(distanceData)
      .select()
    
    if (error) {
      console.error('Error creating event distances:', error)
      throw error
    }
    
    return data || []
  },

  // Update event distances
  async updateEventDistances(eventId: string, distances: Array<{
    id?: string;
    name: string;
    length: string;
    price: number;
    description?: string;
    age_restriction?: string;
    difficulty?: string;
  }>): Promise<EventDistance[]> {
    // First, delete all existing distances for this event
    await supabase
      .from('event_distances')
      .delete()
      .eq('event_id', eventId);

    // Then create new distances
    return this.createEventDistances(eventId, distances);
  }
}

// Registration operations
export const registrationService = {
  // Get all registrations for an event
  async getEventRegistrations(eventId: string): Promise<Registration[]> {
    const { data, error } = await supabase
      .from('registrations')
      .select('*')
      .eq('event_id', eventId)
      .order('registration_date', { ascending: false })
    
    if (error) {
      console.error('Error fetching registrations:', error)
      throw error
    }
    
    return data || []
  },

  // Create new registration
  async createRegistration(registration: Omit<Registration, 'id' | 'registration_date'>): Promise<Registration> {
    const { data, error } = await supabase
      .from('registrations')
      .insert([{
        ...registration,
        registration_date: new Date().toISOString()
      }])
      .select()
      .single()
    
    if (error) {
      console.error('Error creating registration:', error)
      throw error
    }
    
    return data
  },

  // Update registration status
  async updateRegistrationStatus(id: string, status: Registration['status']): Promise<Registration> {
    const { data, error } = await supabase
      .from('registrations')
      .update({ status })
      .eq('id', id)
      .select()
      .single()
    
    if (error) {
      console.error('Error updating registration:', error)
      throw error
    }
    
    return data
  },

  // Delete registration
  async deleteRegistration(id: string): Promise<void> {
    const { error } = await supabase
      .from('registrations')
      .delete()
      .eq('id', id)
    
    if (error) {
      console.error('Error deleting registration:', error)
      throw error
    }
  },

  // Check if user is already registered for an event
  async checkExistingRegistration(eventId: string, email: string): Promise<Registration | null> {
    const { data, error } = await supabase
      .from('registrations')
      .select('*')
      .eq('event_id', eventId)
      .eq('user_email', email)
      .single()
    
    if (error && error.code !== 'PGRST116') { // PGRST116 is "not found" error
      console.error('Error checking registration:', error)
      throw error
    }
    
    return data
  },
  
  async getExistingRegistrations(eventId: string, email: string): Promise<Registration[]> {
    const { data, error } = await supabase
      .from('registrations')
      .select('*')
      .eq('event_id', eventId)
      .eq('user_email', email)
    
    if (error) {
      console.error('Error getting existing registrations:', error)
      throw error
    }
    
    return data || []
  },

  // Get all registrations across all events (for admin)
  async getAllRegistrations(): Promise<(Registration & { event_title: string })[]> {
    const { data, error } = await supabase
      .from('registrations')
      .select(`
        *,
        events (
          title
        )
      `)
      .order('registration_date', { ascending: false })
    
    if (error) {
      console.error('Error fetching all registrations:', error)
      throw error
    }
    
    // Transform the data to include event title
    return (data || []).map(registration => ({
      ...registration,
      event_title: registration.events?.title || 'Unknown Event'
    }))
  }
}

// User operations
export const userService = {
  // Get user by email
  async getUserByEmail(email: string): Promise<User | null> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('email', email)
      .single()
    
    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching user:', error)
      throw error
    }
    
    return data
  },

  // Get user by ID
  async getUserById(id: string): Promise<User | null> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', id)
      .single()
    
    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching user:', error)
      throw error
    }
    
    return data
  },

  // Create new user
  async createUser(user: Omit<User, 'id' | 'created_at'>): Promise<User> {
    const { data, error } = await supabase
      .from('users')
      .insert([user])
      .select()
      .single()
    
    if (error) {
      console.error('Error creating user:', error)
      throw error
    }
    
    return data
  },

  // Create new user with flexible fields (works with any user table structure)
  async createUserFlexible(userData: any): Promise<any> {
    const { data, error } = await supabase
      .from('users')
      .insert([userData])
      .select()
      .single()
    
    if (error) {
      console.error('Error creating user:', error)
      throw error
    }
    
    return data
  },

  // Update user profile
  async updateUser(id: string, updates: Partial<User>): Promise<User> {
    const { data, error } = await supabase
      .from('users')
      .update(updates)
      .eq('id', id)
      .select()
      .single()
    
    if (error) {
      console.error('Error updating user:', error)
      throw error
    }
    
    return data
  },


}

// Minor operations
export const minorService = {
  // Get all minors for a user
  async getUserMinors(userId: string): Promise<Minor[]> {
    const { data, error } = await supabase
      .from('minors')
      .select('*')
      .eq('user_id', userId)
      .order('name', { ascending: true })
    
    if (error) {
      console.error('Error fetching minors:', error)
      throw error
    }
    
    return data || []
  },

  // Create new minor
  async createMinor(minor: Omit<Minor, 'id' | 'created_at'>): Promise<Minor> {
    const { data, error } = await supabase
      .from('minors')
      .insert([minor])
      .select()
      .single()
    
    if (error) {
      console.error('Error creating minor:', error)
      throw error
    }
    
    return data
  },

  // Update minor
  async updateMinor(id: string, updates: Partial<Minor>): Promise<Minor> {
    const { data, error } = await supabase
      .from('minors')
      .update(updates)
      .eq('id', id)
      .select()
      .single()
    
    if (error) {
      console.error('Error updating minor:', error)
      throw error
    }
    
    return data
  },

  // Delete minor
  async deleteMinor(id: string): Promise<void> {
    const { error } = await supabase
      .from('minors')
      .delete()
      .eq('id', id)
    
    if (error) {
      console.error('Error deleting minor:', error)
      throw error
    }
  },


} 